import InstallPromptContext, {
	type BeforeInstallPromptEvent,
	type InstallPromptContextValue,
} from '@UI/Providers/InstallPromptProvider/InstallPromptContext';
import * as React from 'react';

/**
 * The `in` operator reads as a plain runtime check, not a cast: it lets the listener stay typed as
 * a regular `(event: Event) => void` — assignable without `as` — while narrowing to the richer local
 * type wherever the extra `prompt()`/`userChoice` are actually read.
 */
const isBeforeInstallPromptEvent = (event: Event): event is BeforeInstallPromptEvent => {
	return 'prompt' in event && 'userChoice' in event;
};

type Props = {
	readonly children: React.ReactNode;
};

/**
 * The browser fires `beforeinstallprompt` at most once per page load, and DOM events are never
 * replayed. Capturing it in a single listener installed once here, and sharing the captured event
 * through context, is what lets a consumer mounted after the fact — a settings screen reached by
 * later navigation, for instance — still see it: a listener installed per hook instance would only
 * ever catch the firing that happens to occur after that particular instance mounts.
 */
const InstallPromptProvider: React.FC<Props> = ({ children }) => {
	const [deferredEvent, setDeferredEvent] = React.useState<BeforeInstallPromptEvent | null>(null);

	React.useEffect(() => {
		const handleBeforeInstallPrompt = (event: Event): void => {
			if (!isBeforeInstallPromptEvent(event)) {
				return;
			}

			event.preventDefault();
			setDeferredEvent(event);
		};

		const handleAppInstalled = (): void => {
			setDeferredEvent(null);
		};

		window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
		window.addEventListener('appinstalled', handleAppInstalled);

		return () => {
			window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	const clearDeferredEvent = React.useCallback((): void => {
		setDeferredEvent(null);
	}, []);

	const value = React.useMemo<InstallPromptContextValue>(
		() => ({ deferredEvent, clearDeferredEvent }),
		[deferredEvent, clearDeferredEvent],
	);

	return <InstallPromptContext.Provider value={value}>{children}</InstallPromptContext.Provider>;
};

export default InstallPromptProvider;
